import { useEffect, useState } from "react";
import { FileDown, FileSpreadsheet, Search } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Empty, Field, NSelect, PageHeader } from "@/components/common";
import { useFleet } from "@/context/FleetContext";
import { api, download, errMsg } from "@/lib/api";
import { dateInput, daysAgo, formatCell, toIso } from "@/lib/fleet";
import { cn } from "@/lib/utils";

const TYPES = [["mileage", "Daily Mileage"], ["trips", "Trip Report"], ["stops", "Stop Report"], ["overspeed", "Overspeed"], ["alarms", "Alarm Report"], ["geofence", "Geofence Report"], ["activity", "Vehicle Activity"]];
const PERIODS = [["today", "Hari ini", 0], ["yesterday", "Kemarin", 1], ["7d", "7 hari", 6], ["30d", "30 hari", 29], ["custom", "Custom", null]];

export default function Reports() {
  const { vehicles } = useFleet();
  const [type, setType] = useState("mileage");
  const [vid, setVid] = useState("");
  const [period, setPeriod] = useState("7d");
  const [from, setFrom] = useState(dateInput(daysAgo(6)));
  const [to, setTo] = useState(dateInput());
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);

  const pickPeriod = (k, n) => {
    setPeriod(k);
    if (n == null) return;
    setFrom(dateInput(daysAgo(n)));
    setTo(dateInput(k === "yesterday" ? daysAgo(1) : new Date()));
  };
  const params = () => ({ start: toIso(from, "00:00"), end: toIso(to, "23:59"), vehicle_id: vid || undefined });

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reports/${type}`, { params: params() });
      setRep(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [type]);

  const exp = async (format) => {
    try {
      await download(`/reports/${type}/export`, { ...params(), format }, `glazy-${type}-${from}.${format}`);
    } catch (e) {
      toast.error("Gagal export laporan");
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Laporan" title="Laporan Armada" subtitle="Filter berdasarkan kendaraan, tanggal dan periode. Export ke PDF atau Excel (CSV)."
        actions={<>
          <button onClick={() => exp("pdf")} className="flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-bold hover:border-primary" data-testid="report-export-pdf"><FileDown className="h-4 w-4" /> PDF</button>
          <button onClick={() => exp("csv")} className="flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground" data-testid="report-export-csv"><FileSpreadsheet className="h-4 w-4" /> Excel / CSV</button>
        </>} />
      <div className="gt-noscroll -mx-4 mb-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        {TYPES.map(([k, l]) => (
          <button key={k} onClick={() => setType(k)} data-testid={`report-type-${k}`}
            className={cn("shrink-0 rounded-xl border px-4 py-2.5 text-[13px] font-semibold transition-colors", type === k ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground")}>{l}</button>
        ))}
      </div>
      <div className="gt-card mb-5 grid gap-3 p-4 md:grid-cols-[1.2fr_1.5fr_1fr_1fr_auto] md:items-end">
        <Field label="Kendaraan">
          <NSelect value={vid} onChange={setVid} testId="report-vehicle-select"><option value="">Semua kendaraan</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.plate}</option>)}</NSelect>
        </Field>
        <Field label="Periode">
          <div className="flex rounded-xl border border-border p-1">
            {PERIODS.map(([k, l, n]) => <button key={k} onClick={() => pickPeriod(k, n)} className={cn("flex-1 rounded-lg px-1 py-1.5 text-[11px] font-semibold", period === k ? "bg-foreground text-background" : "text-muted-foreground")} data-testid={`report-period-${k}`}>{l}</button>)}
          </div>
        </Field>
        <Field label="Dari"><input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPeriod("custom"); }} className="gt-input" data-testid="report-from-date" /></Field>
        <Field label="Sampai"><input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPeriod("custom"); }} className="gt-input" data-testid="report-to-date" /></Field>
        <button onClick={run} className="flex h-10 items-center justify-center gap-2 rounded-xl bg-primary px-5 text-sm font-bold text-primary-foreground" data-testid="report-run-btn"><Search className="h-4 w-4" /> {loading ? "..." : "Tampilkan"}</button>
      </div>
      {rep && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div><div className="text-lg font-extrabold" data-testid="report-title">{rep.title}</div><div className="text-xs text-muted-foreground">{rep.rows.length} baris{rep.summary.distance_km != null ? ` · total ${rep.summary.distance_km} km` : ""}</div></div>
          </div>
          {type === "mileage" && rep.rows.length > 0 && (
            <div className="gt-card h-[240px] p-4">
              <ResponsiveContainer>
                <BarChart data={Object.values(rep.rows.reduce((a, r) => { a[r.date] = a[r.date] || { date: r.date, km: 0 }; a[r.date].km += r.distance_km; return a; }, {})).sort((a, b) => a.date.localeCompare(b.date))}>
                  <XAxis dataKey="date" tickFormatter={(x) => x.slice(5)} stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Tooltip formatter={(v) => `${v.toFixed(1)} km`} contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
                  <Bar dataKey="km" fill="#EAB308" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <div className="gt-card overflow-hidden" data-testid="report-table">
            <div className="gt-scroll max-h-[560px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card"><tr className="border-b border-border text-left">{rep.columns.map((c) => <th key={c.key} className="gt-eyebrow whitespace-nowrap px-4 py-3">{c.label}</th>)}</tr></thead>
                <tbody>
                  {rep.rows.map((r, i) => (
                    <tr key={i} className="border-b border-border/50 hover:bg-accent/40">
                      {rep.columns.map((c) => <td key={c.key} className={cn("whitespace-nowrap px-4 py-2.5", c.fmt !== "text" && "font-mono text-[12.5px]")}>{formatCell(r[c.key], c.fmt)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
              {!rep.rows.length && <Empty title="Tidak ada data" text="Tidak ada data untuk filter ini." />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
